import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { BlueprintChat, type BlueprintQuestion } from "@/components/blueprint/BlueprintChat";
import { useCreateGoal, useGoals, useSaveGoalProfile } from "@/lib/queries";
import { TIER_2_OPENER, splitList, useBlueprintSave } from "@/lib/blueprint";

/**
 * 7c — the funding blueprint.
 *
 * Fills `fundraising_profiles`. The questions are chosen for what actually
 * separates one investor room from another: stage and traction decide who will
 * even take the meeting, the raise decides which fund size is a fit, and the
 * funds already on her radar are the only answer here that names verified
 * targets — which makes it the most valuable one in the set.
 *
 * Deliberately *not* asked: anything a deck would answer better, and anything
 * with a number she would have to look up. A founder does this on a phone.
 */
export const Route = createFileRoute("/funding")({
  validateSearch: z.object({ tier: z.literal(2).optional() }),
  component: Funding,
});

/**
 * Three questions, not five.
 *
 * The conversation is the gate on the feed now, so its length is the price of
 * seeing the rooms — and a five-question price on a screen she reached by
 * tapping "unlock" is where she closes the tab. Three she finishes.
 *
 * The three that stayed are the three the ranking reads. What went: `traction` and `fundsOnRadar` — the one-line pitch and the names already on her list. `fundsOnRadar` is the most useful answer in the set and belongs in the deeper pass, where she is naming funds because she has already seen the map work.
 */
const TIER_1: BlueprintQuestion[] = [
  {
    field: "stage",
    ask: "Where are you in the raise right now?",
    choices: [
      { value: "not raising yet", label: "Not raising yet" },
      { value: "raising now", label: "Raising now" },
      { value: "always talking", label: "Always talking" },
    ],
    placeholder: "Pick one above",
  },
  {
    field: "targetRaiseAmount",
    ask: "How much are you looking to raise, and at what stage?",
    hint: "“about 500k pre-seed”. A range is fine.",
    placeholder: "About 500k pre-seed…",
  },
  {
    field: "preferredInvestorTypes",
    ask: "Last one. Who do you actually want money from? Angels, micro-VCs, sector specialists, generalists, corporates.",
    hint: "Name the ones you would take a call from this week.",
    placeholder: "Angels and sector specialists…",
  },
];

/**
 * Tier two — what an investor asks on the second call.
 *
 * `previousRaises`, `hasAngels`, `useOfFunds` and `pitchDeckUrl` are real
 * columns tier one left empty; the last question has no column and lands in
 * `extra`, because "who said no and why" is the most useful thing a founder
 * knows about her own raise and there is nowhere typed to put it yet.
 */
const TIER_2: BlueprintQuestion[] = [
  {
    field: "hasAngels",
    ask: "Do you already have angels in, or would this be the first outside money?",
    choices: [
      { value: "yes", label: "Angels already in" },
      { value: "no", label: "First outside money" },
    ],
    placeholder: "Pick one above",
  },
  {
    field: "previousRaises",
    ask: "What have you raised so far, and from whom?",
    hint: "Rough is fine — “80k from two angels last year”. Say none if none.",
    placeholder: "None yet…",
  },
  {
    field: "useOfFunds",
    ask: "What does this round actually buy? The specific thing, not the category.",
    hint: "“two engineers and eighteen months” beats “growth”.",
    placeholder: "Two engineers and eighteen months of runway…",
  },
  {
    field: "passReasons",
    ask: "Has anyone passed already? What did they say?",
    hint: "The most useful answer in this set. A pass names the room you should not be walking into.",
    placeholder: "Too early, no revenue yet…",
  },
  {
    field: "pitchDeckUrl",
    ask: "Last one. Link to your deck, if it is somewhere shareable.",
    hint: "Skip it if not. Nothing here depends on it.",
    placeholder: "https://…",
  },
];

function Funding() {
  const { tier } = Route.useSearch();
  const isDeep = tier === 2;

  const goals = useGoals();
  const createGoal = useCreateGoal();
  const saveGoalProfile = useSaveGoalProfile();
  const navigate = useNavigate();
  const save = useBlueprintSave({
    type: "fund",
    goals,
    createGoal,
    saveGoalProfile,
    /**
     * The feed, not the five.
     *
     * She has just spent a conversation describing exactly who she sells to, and
     * `/matches` answers that with three rooms and two behind a lock — the screen
     * whose job is to prove the product works to someone who has not paid, and
     * whose next control is a button back to the radar to scan again. Landing
     * there after the questions reads as being sent to the queue after doing the
     * work.
     *
     * The feed is what the answers actually bought: every room that ranks, in the
     * order the blueprint just changed.
     */
    onDone: () => void navigate({ to: "/feed" }),
  });

  return (
    <BlueprintChat
      title={isDeep ? "Sharpening your matches" : "Your funding blueprint"}
      opener={
        isDeep
          ? TIER_2_OPENER
          : "A few questions to unlock better matches, based on what you actually need. Answer them out loud or type — either is fine."
      }
      questions={isDeep ? TIER_2 : TIER_1}
      saving={save.saving}
      error={save.error}
      doneTitle="That is the blueprint."
      doneNote={
        isDeep
          ? "Omina now knows the history, the ask and the passes. The next scan is ranked against all of it."
          : "It sets what Omina looks for on this track: rooms where the people who write cheques actually are."
      }
      saveLabel={isDeep ? "Save and rescan" : "Save and see my rooms"}
      /* The feed either way — it is where both tiers are entered from now.
         Tier one used to return to `/matches`, which was true when the nudge
         lived there and is a detour now. */
      exitTo="/feed"
      onSave={(a) =>
        isDeep
          ? save.run({
              // A yes/no answer stored as a boolean, and only when she gave
              // one: coercing a missing answer into `false` would write "no
              // angels" as a fact she never stated.
              ...(a.hasAngels ? { hasAngels: a.hasAngels === "yes" } : {}),
              ...(a.previousRaises ? { previousRaises: [{ note: a.previousRaises }] } : {}),
              ...(a.useOfFunds ? { useOfFunds: a.useOfFunds } : {}),
              ...(a.pitchDeckUrl ? { pitchDeckUrl: a.pitchDeckUrl } : {}),
              ...(a.passReasons ? { extra: { passReasons: a.passReasons } } : {}),
            })
          : save.run({
              ...(a.stage ? { stage: a.stage } : {}),
              ...(a.targetRaiseAmount ? { targetRaiseAmount: a.targetRaiseAmount } : {}),
              ...(a.traction ? { traction: a.traction } : {}),
              ...(splitList(a.preferredInvestorTypes)
                ? { preferredInvestorTypes: splitList(a.preferredInvestorTypes) }
                : {}),
              ...(splitList(a.fundsOnRadar) ? { fundsOnRadar: splitList(a.fundsOnRadar) } : {}),
            })
      }
    />
  );
}
